/**
 * Central routing. Protected routes require auth; layout uses bottom tab bar.
 */
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthProvider';
import { MainLayout } from '../shared/ui/MainLayout';
import { HomePage } from '../features/feed/HomePage';
import { LeaderboardPage } from '../features/leaderboard/LeaderboardPage';
import { PublicLeaderboardPage } from '../features/leaderboard/PublicLeaderboardPage';
import { AddLiftPage } from '../features/workouts/AddLiftPage';
import { ProfilePage } from '../features/profile/ProfilePage';
import { ProfileViewPage } from '../features/profile/ProfileViewPage';
import { CompleteProfilePage } from '../features/profile/CompleteProfilePage';
import { ComparePage } from '../features/compare/ComparePage';
import { CompareSelectPage } from '../features/compare/CompareSelectPage';
import { SearchPage } from '../features/search/SearchPage';
import { ChallengesPage } from '../features/challenges/ChallengesPage';
import { ChallengeDetailPage } from '../features/challenges/ChallengeDetailPage';
import { CoachingPage } from '../features/coaching/CoachingPage';
import { CoachProfilePage } from '../features/coaching/CoachProfilePage';
import { MyProgramsPage } from '../features/coaching/MyProgramsPage';
import { MyCoachingPlanPage } from '../features/coaching/MyCoachingPlanPage';
import { MyRequestsPage } from '../features/coaching/MyRequestsPage';
import { ProgramViewPage } from '../features/coaching/ProgramViewPage';
import { MyCoachPage } from '../features/coaching/MyCoachPage';
import { SubscriptionPage } from '../features/subscription/SubscriptionPage';
import { LoginPage } from '../features/auth/LoginPage';
import { SignUpPage } from '../features/auth/SignUpPage';
import { isProfileComplete } from '../domain/entities/User';

function AuthLoadingScreen({ message = 'Loading…' }) {
  return (
    <MainLayout>
      <div className="screen-center" style={{ flexDirection: 'column', gap: '1rem' }}>
        <div className="skeleton-card" />
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{message}</p>
      </div>
    </MainLayout>
  );
}

function ProtectedRoute({ children }) {
  const { user, profile, loading } = useAuth();
  if (loading) return <AuthLoadingScreen message="Loading your profile…" />;
  if (!user) return <Navigate to="/login" replace />;
  if (profile && !isProfileComplete(profile)) return <CompleteProfilePage />;
  return children;
}

function PublicOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="screen-center"><div className="skeleton-card" /></div>;
  if (user) return <Navigate to="/" replace />;
  return children;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
      <Route path="/signup" element={<PublicOnlyRoute><SignUpPage /></PublicOnlyRoute>} />
      <Route path="/lb" element={<PublicLeaderboardPage />} />
      <Route path="/lb/:lift" element={<PublicLeaderboardPage />} />
      <Route path="/challenges/:challengeId" element={<MainLayout><ChallengeDetailPage /></MainLayout>} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/leaderboard" element={<LeaderboardPage />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/add" element={<AddLiftPage />} />
                <Route path="/challenges" element={<ChallengesPage />} />
                <Route path="/coaching" element={<CoachingPage />} />
                <Route path="/coaching/requests" element={<MyRequestsPage />} />
                <Route path="/coaching/my-coach" element={<MyCoachPage />} />
                <Route path="/coaching/my-plan" element={<MyCoachingPlanPage />} />
                <Route path="/coaching/my-programs" element={<MyProgramsPage />} />
                <Route path="/coaching/my-programs/:programId" element={<ProgramViewPage />} />
                <Route path="/coaching/:coachId" element={<CoachProfilePage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/profile/:userId" element={<ProfileViewPage />} />
                <Route path="/subscription" element={<SubscriptionPage />} />
                <Route path="/compare/select" element={<CompareSelectPage />} />
                <Route path="/compare/:userId" element={<ComparePage />} />
              </Routes>
            </MainLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
